//Npm packages
import axios from 'axios';
import { trackPromise } from 'react-promise-tracker';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import swal from 'sweetalert2';
import strings from '../../globalization';

//Redux actions
import {
  LOGIN,
  LOGOUT,
  UPDATETHEME,
  LICENSEFORM,
  LICENSELIST,
  USERLIST,
  USERFORM,
  RESETPASSWORD,
  FACILITYFORM,
  EQUIPMENTFROM,
  EQUIPMENTLIST,
  HISTORYLIST,
  PREFERENCELIST,
  PREFERENCEFORM,
  REPORTFORM,
  GENERATEREPORT,
  COMBOTYPES,
  COMBOTYPES_SUCCESS,
  RESETPASSWORDREQ,
  TESTACCESSLOCATION,
  MODELFORM,
  MODELLIST,
  COUNTRIES,
  TRACKING_SYSTEM_FORM,
  TRACKING_SYSTEM_LIST,
  CAT_SOFTWARE_VERSION_FORM,
  CAT_SOFTWARE_VERSION_LIST,
  SORTORDERFORM,
  SORTORDERLIST,
  TRACKING_SYSTEM_CONTACT_LIST,
  TRACKING_SYSTEM_CONTACT_FORM
} from '../actions';

//Helper files
import consts from '../../consts';

const ResponseCode = consts.ResponseCode;

/**
 * @desc - axios request
 * @param {Object} ActionInfo -  Object of action that has api path, body
 */
const fetchRequest = ({ apiUrl, data, apiMethod }) =>
  trackPromise(
    axios.request({
      method: apiMethod,
      baseURL: consts.hostURL,
      url: apiUrl,
      withCredentials: true,
      data: data,
      timeout: consts.defaultValue.requestTimeout
    })
  );

/**
 * @desc - axios request
 * @param {Object} ActionInfo -  Object of action that has api path, body
 */
const downloadFetchRequest = ({ apiUrl, data, apiMethod }) =>
  trackPromise(
    axios.request({
      method: apiMethod,
      baseURL: consts.hostURL,
      url: apiUrl,
      withCredentials: true,
      data: data,
      timeout: consts.defaultValue.requestTimeout,
      responseType: 'arraybuffer'
    })
  );
/**
 * Generic request handler for all API calls
 * @param {Object} action - Redux action object
 * @param {Object} action.data - Request data payload
 * @param {boolean} action.data.silent - If true, suppresses success info alerts
 */
export function* request(action) {
  try {
    //Here we are request using action values
    let { data } = yield call(fetchRequest, action);

    //If we get any error in response we show the message
    if (!data.success && data.err && !data.CustomError) {
      let msg = strings[data.err];
      if (!msg) msg = data.err;
      swal.fire(strings.Alert, msg, 'warning');
    }

    //If we get any info in response we show the message apart from Login Action
    //Skip showing alert if silent flag is set (for auto-save operations)
    if (
      data.success &&
      data.info &&
      action.type !== LOGIN &&
      !action.data.silent
    ) {
      let msg = strings[data.info];
      if (!msg) msg = data.info;
      swal.fire(strings.Info, msg, 'info');
    }
    if (data.comboType) {
      yield put({
        type: COMBOTYPES_SUCCESS,
        data: data.comboType
      });
    }
    //From here we dispatch Success and Failure action for the Request
    yield put({
      type: action.type + (data.success ? '_SUCCESS' : '_FAILURE'),
      data
    });
  } catch (error) {
    yield setError(error, action);
  }
}

function* setError(error, action) {
  //reading the HTTP error code
  let errorStatus = error.response
      ? error.response.status
      : ResponseCode.RequestTimeout,
    message = '',
    shouldRedirectToLogin = errorStatus === ResponseCode.SessionExpire;
  switch (errorStatus) {
    case ResponseCode.InternalServerError:
      message = strings.InternalServerError;
      break;
    case ResponseCode.ServerOverload:
      message = strings.ServerOverload;
      break;
    case ResponseCode.ServerUnavailable:
      message = strings.ServerUnavailable;
      break;
    case ResponseCode.SessionExpire:
      message = strings.SessionExpire;
      break;
    case ResponseCode.BadRequest:
      message = strings.BadRequest;
      break;
    case ResponseCode.RequestTimeout:
      message = strings.RequestTimeoutError;
      break;
    default:
      message = strings.UnhandledException;
      break;
  }
  //From here we dispatch Error action for the Request
  yield put({
    type: action.type + '_ERROR',
    data: { success: false, err: error }
  });
  if (shouldRedirectToLogin) {
    yield put({ type: 'LOGIN_RESET', data: null });
  }

  //we show the exception message from here
  //In case of Session Expire we redirect the user to main screen
  if (!action.isFromLoginPage) {
    swal.fire(strings.Error, message, 'error').then(() => {
      if (shouldRedirectToLogin) {
        // eslint-disable-next-line no-undef
        window.location.hash = '#/';
      }
    });
  }
}

export function* loginRequest() {
  yield takeLatest(LOGIN, request);
}

export function* logoutRequest() {
  yield takeLatest(LOGOUT, request);
}

export function* updateThemeRequest() {
  yield takeLatest(UPDATETHEME, request);
}

export function* userListDataRequest() {
  yield takeLatest(USERLIST, request);
}

export function* userFormDataRequest() {
  yield takeLatest(USERFORM, request);
}

export function* licenseListDataRequest() {
  yield takeLatest(LICENSELIST, request);
}

export function* licenseFormDataRequest() {
  yield takeLatest(LICENSEFORM, request);
}

export function* resetPasswordRequest() {
  yield takeLatest(RESETPASSWORD, request);
}

export function* resetPasswordReqRequest() {
  yield takeLatest(RESETPASSWORDREQ, request);
}

export function* facilityFormRequest() {
  yield takeLatest(FACILITYFORM, request);
}

export function* equipmentFormRequest() {
  yield takeLatest(EQUIPMENTFROM, request);
}

export function* equipmentListRequest() {
  yield takeLatest(EQUIPMENTLIST, request);
}

export function* historyListRequest() {
  yield takeLatest(HISTORYLIST, request);
}

export function* preferenceFormRequest() {
  yield takeLatest(PREFERENCEFORM, request);
}

export function* preferenceListRequest() {
  yield takeLatest(PREFERENCELIST, request);
}

export function* reportFormDataRequest() {
  yield takeLatest(REPORTFORM, request);
}

export function* generateReportRequest() {
  yield takeLatest(GENERATEREPORT, request);
}

export function* comboTypes() {
  yield takeLatest(COMBOTYPES, request);
}

export function* testAccessLocation() {
  yield takeLatest(TESTACCESSLOCATION, request);
}

export function* modelFormDataRequest() {
  yield takeLatest(MODELFORM, request);
}

export function* modelListDataRequest() {
  yield takeLatest(MODELLIST, request);
}
export function* countriesListDataRequest() {
  yield takeLatest(COUNTRIES, request);
}

export function* trackingSystemListDataRequest() {
  yield takeLatest(TRACKING_SYSTEM_LIST, request);
}

export function* trackingSystemFormDataRequest() {
  yield takeLatest(TRACKING_SYSTEM_FORM, request);
}

export function* sortOrderFormRequest() {
  yield takeLatest(SORTORDERFORM, request);
}
export function* sortOrderListRequest() {
  yield takeLatest(SORTORDERLIST, request);
}

export function* catSoftwareVersionListDataRequest() {
  yield takeLatest(CAT_SOFTWARE_VERSION_LIST, request);
}
export function* catSoftwareVersionFormDataRequest() {
  yield takeLatest(CAT_SOFTWARE_VERSION_FORM, request);
}

/**
 * Saga watcher for tracking system contact list requests.
 *
 * Listens for the `TRACKING_SYSTEM_CONTACT_LIST` action
 * and triggers the `request` saga to handle the API call.
 *
 * @generator
 * @yields {Object} Redux-Saga effect to watch for the action.
 */
export function* trackingSystemContactListDataRequest() {
  yield takeLatest(TRACKING_SYSTEM_CONTACT_LIST, request);
}

/**
 * Saga watcher for tracking system contact form requests.
 *
 * Listens for the `TRACKING_SYSTEM_CONTACT_FORM` action
 * and triggers the `request` saga to handle the API call.
 *
 * @generator
 * @yields {Object} Redux-Saga effect to watch for the action.
 */
export function* trackingSystemContactFormDataRequest() {
  yield takeLatest(TRACKING_SYSTEM_CONTACT_FORM, request);
}

export default function* rootSaga() {
  yield all([
    loginRequest(),
    logoutRequest(),
    updateThemeRequest(),
    userListDataRequest(),
    userFormDataRequest(),
    licenseListDataRequest(),
    licenseFormDataRequest(),
    resetPasswordRequest(),
    resetPasswordReqRequest(),
    facilityFormRequest(),
    equipmentFormRequest(),
    equipmentListRequest(),
    historyListRequest(),
    preferenceFormRequest(),
    preferenceListRequest(),
    reportFormDataRequest(),
    generateReportRequest(),
    comboTypes(),
    testAccessLocation(),
    modelListDataRequest(),
    modelFormDataRequest(),
    countriesListDataRequest(),
    trackingSystemListDataRequest(),
    trackingSystemFormDataRequest(),
    catSoftwareVersionListDataRequest(),
    catSoftwareVersionFormDataRequest(),
    sortOrderFormRequest(),
    sortOrderListRequest(),
    trackingSystemContactListDataRequest(),
    trackingSystemContactFormDataRequest()
  ]);
}
